import { useEffect, useState } from 'react';
import { AuditActionOption, useAuditService } from '../../components/services/auditService';

export interface AuditActions {
  /** Every action, sorted by customer-facing name (as the API returns them). */
  options: AuditActionOption[];
  /** Recorded code -> customer-facing name. Codes not in the list (e.g. a newer service's action) show as-is. */
  nameByCode: Record<string, string>;
}

const EMPTY: AuditActions = { options: [], nameByCode: {} };

/** The action list for the Action filter and column, from AuditAPI (Defatom.Shared AuditActionEnum). Loaded once. */
export const useAuditActions = (): AuditActions => {
  const { getActions } = useAuditService();
  const [actions, setActions] = useState<AuditActions>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    getActions()
      .then((response) => {
        if (cancelled || !response?.isSuccess || !Array.isArray(response.data)) return;
        setActions({
          options: response.data,
          nameByCode: Object.fromEntries(response.data.map((option) => [option.action, option.displayName])),
        });
      })
      .catch(() => {
        // The shared API interceptor has already shown the error; the page falls back to raw action codes.
      });
    return () => {
      cancelled = true;
    };
  }, [getActions]);

  return actions;
};

/** Customer-facing name for a recorded action code, or the code itself when it isn't in the list. */
export const actionDisplayName = (code: string, nameByCode: Record<string, string>): string => nameByCode[code] || code;

export default useAuditActions;
